import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Button, Table, Badge, Spinner, Alert, Tabs, Tab, Modal, Form, Pagination } from 'react-bootstrap';
import apiService from '../services/api';

const Billing = () => {
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('invoices');
  const [showMatchModal, setShowMatchModal] = useState(false);
  const [selectedInvoiceToMatch, setSelectedInvoiceToMatch] = useState(null);
  const [selectedErpInvoice, setSelectedErpInvoice] = useState('');
  const [updatingBillingData, setUpdatingBillingData] = useState(false);
  const [billingDataUpdateMessage, setBillingDataUpdateMessage] = useState(null);
  const [erpInvoices, setErpInvoices] = useState([]);
  const [loadingErpInvoices, setLoadingErpInvoices] = useState(false);
  const [erpInvoicesError, setErpInvoicesError] = useState('');
  const [erpCustomers, setErpCustomers] = useState([]);
  const [matchingInvoice, setMatchingInvoice] = useState(false);
  const [matchError, setMatchError] = useState('');
  const [selectedOrganization, setSelectedOrganization] = useState('');

  // Sorting state
  const [invoiceSort, setInvoiceSort] = useState({ column: null, direction: 'asc' });
  const [paymentSort, setPaymentSort] = useState({ column: null, direction: 'asc' });

  // Pagination state
  const [invoicePage, setInvoicePage] = useState(1);
  const [paymentPage, setPaymentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    loadBillingData();
  }, []);

  // Get unique organizations from invoices and payments
  const getUniqueOrganizations = () => {
    const organizations = new Map();

    invoices.forEach(invoice => {
      if (invoice.organization_id && invoice.organization_name) {
        organizations.set(invoice.organization_id, invoice.organization_name);
      }
    });

    payments.forEach(payment => {
      if (payment.organization_id && payment.organization_name) {
        organizations.set(payment.organization_id, payment.organization_name);
      }
    });

    return Array.from(organizations, ([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  };

  // Reset pagination when organization filter changes
  useEffect(() => {
    setInvoicePage(1);
    setPaymentPage(1);
  }, [selectedOrganization]);

  // Filter invoices and payments by selected organization
  let filteredInvoices = selectedOrganization
    ? invoices.filter(invoice => String(invoice.organization_id) === String(selectedOrganization))
    : [...invoices];

  if (invoiceSort.column) {
    filteredInvoices = [...filteredInvoices].sort((a, b) => {
      let aVal = a[invoiceSort.column];
      let bVal = b[invoiceSort.column];
      if (aVal === undefined || aVal === null) aVal = '';
      if (bVal === undefined || bVal === null) bVal = '';
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      if (aVal < bVal) return invoiceSort.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return invoiceSort.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }

  let filteredPayments = selectedOrganization
    ? payments.filter(payment => String(payment.organization_id) === String(selectedOrganization))
    : [...payments];

  if (paymentSort.column) {
    filteredPayments = [...filteredPayments].sort((a, b) => {
      let aVal = a[paymentSort.column];
      let bVal = b[paymentSort.column];
      if (aVal === undefined || aVal === null) aVal = '';
      if (bVal === undefined || bVal === null) bVal = '';
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      if (aVal < bVal) return paymentSort.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return paymentSort.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }

  // Pagination calculations
  const invoiceTotalPages = Math.ceil(filteredInvoices.length / itemsPerPage);
  const paginatedInvoices = filteredInvoices.slice((invoicePage - 1) * itemsPerPage, invoicePage * itemsPerPage);

  const paymentTotalPages = Math.ceil(filteredPayments.length / itemsPerPage);
  const paginatedPayments = filteredPayments.slice((paymentPage - 1) * itemsPerPage, paymentPage * itemsPerPage);

  const handleInvoiceSort = (column) => {
    setInvoicePage(1);
    setInvoiceSort(prev => {
      if (prev.column === column) {
        return { column, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { column, direction: 'asc' };
    });
  };

  const handlePaymentSort = (column) => {
    setPaymentPage(1);
    setPaymentSort(prev => {
      if (prev.column === column) {
        return { column, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { column, direction: 'asc' };
    });
  };

  const renderPagination = (currentPage, totalPages, setPage) => {
    if (totalPages <= 1) return null;
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }

    return (
      <div className="d-flex justify-content-between align-items-center px-3 py-2">
        <small className="text-muted">
          Page {currentPage} of {totalPages}
        </small>
        <Pagination className="mb-0" size="sm">
          <Pagination.First onClick={() => setPage(1)} disabled={currentPage === 1} />
          <Pagination.Prev onClick={() => setPage(currentPage - 1)} disabled={currentPage === 1} />
          {start > 1 && <Pagination.Ellipsis disabled />}
          {Array.from({ length: end - start + 1 }, (_, i) => start + i).map(page => (
            <Pagination.Item key={page} active={page === currentPage} onClick={() => setPage(page)}>
              {page}
            </Pagination.Item>
          ))}
          {end < totalPages && <Pagination.Ellipsis disabled />}
          <Pagination.Next onClick={() => setPage(currentPage + 1)} disabled={currentPage === totalPages} />
          <Pagination.Last onClick={() => setPage(totalPages)} disabled={currentPage === totalPages} />
        </Pagination>
      </div>
    );
  };

  const loadBillingData = async () => {
    setLoading(true);
    setError('');

    try {
      const [invoicesResponse, paymentsResponse] = await Promise.all([
        apiService.getInvoices(),
        apiService.getPayments()
      ]);

      if (invoicesResponse.success) {
        console.log('Invoices data:', invoicesResponse.data);
        setInvoices(invoicesResponse.data || []);
      }
      if (paymentsResponse.success) {
        console.log('Payments data:', paymentsResponse.data);
        setPayments(paymentsResponse.data || []);
      }
    } catch (error) {
      console.error('Error loading billing data:', error);
      setError('Failed to load billing data');
    } finally {
      setLoading(false);
    }
  };

  const getInvoiceStatusBadge = (status) => {
    const variants = {
      'paid': 'success',
      'pending': 'warning',
      'overdue': 'danger',
      'cancelled': 'secondary'
    };
    return variants[status] || 'secondary';
  };

  const getPaymentStatusBadge = (status) => {
    const variants = {
      'completed': 'success',
      'pending': 'warning',
      'failed': 'danger',
      'refunded': 'info'
    };
    return variants[status] || 'secondary';
  };

  const handleMatchInvoice = async (invoice) => {
    setSelectedInvoiceToMatch(invoice);
    setShowMatchModal(true);
    setSelectedErpInvoice('');
    await loadErpInvoices(invoice.organization_erp_id);
  };

  const loadErpInvoices = async (organizationErpId) => {
    setLoadingErpInvoices(true);
    setErpInvoicesError('');

    try {
      const [invoicesResponse, customersResponse] = await Promise.all([
        apiService.getErpInvoices(),
        apiService.getErpCustomers()
      ]);

      if (invoicesResponse.success) {
        console.log('ERP invoices data:', invoicesResponse.data);
        const allInvoices = invoicesResponse.data || [];

        // Filter invoices by organization
        const filteredInvoices = organizationErpId && customersResponse.success
          ? allInvoices.filter(invoice => {
            const customer = (customersResponse.data || []).find(c => c.id === invoice.socid);
            return customer && customer.code_client === organizationErpId;
          })
          : allInvoices;

        setErpInvoices(filteredInvoices);
      } else {
        setErpInvoicesError(invoicesResponse.error || 'Failed to load ERP invoices');
      }

      if (customersResponse.success) {
        console.log('ERP customers data:', customersResponse.data);
        setErpCustomers(customersResponse.data || []);
      }
    } catch (error) {
      console.error('Error loading ERP invoices:', error);
      setErpInvoicesError('Failed to load ERP invoices');
    } finally {
      setLoadingErpInvoices(false);
    }
  };

  const getCustomerNameBySocid = (socid) => {
    const customer = erpCustomers.find(c => c.id === socid);
    //console.log('Finding customer for socid:', socid, 'Found:', customer);
    return customer ? customer.name : 'Unknown Customer';
  };

  const handleMatchConfirm = async () => {
    if (!selectedInvoiceToMatch || !selectedErpInvoice) {
      setMatchError('Please select an ERP invoice to match');
      return;
    }

    setMatchingInvoice(true);
    setMatchError('');

    try {
      // Find the selected ERP invoice object
      const selectedErpInvoiceObj = erpInvoices.find(inv => inv.id === selectedErpInvoice);

      if (!selectedErpInvoiceObj) {
        setMatchError('Selected ERP invoice not found');
        setMatchingInvoice(false);
        return;
      }

      const payload = {
        invoice_id: selectedInvoiceToMatch.id,
        erp_invoice_id: selectedErpInvoiceObj.id,
        erp_invoice_ref: selectedErpInvoiceObj.ref,
        erp_invoice_amount: selectedErpInvoiceObj.total_ttc,
        erp_invoice_date: apiService.formatDate(selectedErpInvoiceObj.date),
        erp_invoice_due_date: apiService.formatDate(selectedErpInvoiceObj.date_lim_reglement),
        erp_invoice_validation_date: apiService.formatDate(selectedErpInvoiceObj.date_validation),
        erp_invoice_paid: selectedErpInvoiceObj.paye ? true : false
      };

      console.log('Matching invoice with payload:', payload);
      const response = await apiService.post('/erp_invoice_match', payload);

      if (response.success) {
        // Close modal and refresh data
        setShowMatchModal(false);
        setSelectedInvoiceToMatch(null);
        setSelectedErpInvoice('');
        setErpInvoices([]);
        setErpCustomers([]);

        // Refresh billing data to show updated invoice
        await loadBillingData();
      } else {
        setMatchError(response.error || 'Failed to match invoice');
      }
    } catch (error) {
      console.error('Error matching invoice:', error);
      setMatchError('Failed to match invoice. Please try again.');
    } finally {
      setMatchingInvoice(false);
    }
  };

  const handleUpdateBillingDataFromErp = async () => {
    setUpdatingBillingData(true);
    setBillingDataUpdateMessage(null);

    try {
      const response = await apiService.updateBillingDataFromErp();

      if (response.success) {
        setBillingDataUpdateMessage({ type: 'success', text: 'Billing data updated successfully from ERP' });
        await loadBillingData();
      } else {
        setBillingDataUpdateMessage({ type: 'danger', text: response.error || 'Failed to update Billing Data from ERP' });
      }
    } catch (error) {
      console.error('Error updating Billing Data from ERP:', error);
      setBillingDataUpdateMessage({ type: 'danger', text: 'Failed to update Billing Data from ERP' });
    } finally {
      setUpdatingBillingData(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div>
      <Row>
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>
              <i className="bi bi-receipt me-2"></i>
              Billing
            </h2>
            <Button variant="primary">
              <i className="bi bi-plus-circle me-2"></i>
              Create Invoice
            </Button>
          </div>
        </Col>
      </Row>

      {error && (
        <Row className="mb-4">
          <Col>
            <Alert variant="danger">
              <i className="bi bi-exclamation-triangle me-2"></i>
              {error}
            </Alert>
          </Col>
        </Row>
      )}

      <Row className="mb-3">
        <Col md={4}>
          <Form.Group>
            <Form.Label className="fw-medium">
              <i className="bi bi-building me-2"></i>
              Filter by Organization
            </Form.Label>
            <Form.Select
              value={selectedOrganization}
              onChange={(e) => setSelectedOrganization(e.target.value)}
            >
              <option value="">All Organizations</option>
              {getUniqueOrganizations().map((org) => (
                <option key={org.id} value={org.id}>
                  {org.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={2}>
          <Form.Group>
            <Form.Label className="fw-medium">
              <i className="bi bi-list-ol me-2"></i>
              Rows per page
            </Form.Label>
            <Form.Select
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setInvoicePage(1);
                setPaymentPage(1);
              }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-4">
        <Tab eventKey="invoices" title={`Invoices (${filteredInvoices.length})`}>
          {filteredInvoices.length > 0 && filteredInvoices.every(invoice => invoice.erp_id) && (
            <div className="d-flex justify-content-end mb-3">
              <Button variant="primary" onClick={handleUpdateBillingDataFromErp} disabled={updatingBillingData}>
                <i className="bi bi-arrow-clockwise me-2"></i>
                {updatingBillingData ? 'Updating...' : 'Retrieve Billing Data'}
              </Button>
            </div>
          )}
          {billingDataUpdateMessage && (
            <Alert
              variant={billingDataUpdateMessage.type}
              dismissible
              onClose={() => setBillingDataUpdateMessage(null)}
              className="mb-3"
            >
              <i className={`bi bi-${billingDataUpdateMessage.type === 'success' ? 'check-circle' : 'exclamation-triangle'} me-2`}></i>
              {billingDataUpdateMessage.text}
            </Alert>
          )}
          <Card>
            <Card.Body className="p-0">
              {filteredInvoices.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  <i className="bi bi-receipt display-1"></i>
                  <div className="mt-2">{selectedOrganization ? 'No invoices found for this organization' : 'No invoices found'}</div>
                </div>
              ) : (
                <Table responsive hover className="mb-0">
                  <thead>
                    <tr>
                      <th style={{ cursor: 'pointer' }} onClick={() => handleInvoiceSort('erp_id')}>
                        ERP Invoice # {invoiceSort.column === 'erp_id' ? (invoiceSort.direction === 'asc' ? '▲' : '▼') : ''}
                      </th>
                      <th style={{ cursor: 'pointer' }} onClick={() => handleInvoiceSort('organization_name')}>
                        Organization {invoiceSort.column === 'organization_name' ? (invoiceSort.direction === 'asc' ? '▲' : '▼') : ''}
                      </th>
                      <th style={{ cursor: 'pointer' }} onClick={() => handleInvoiceSort('description')}>
                        Description {invoiceSort.column === 'description' ? (invoiceSort.direction === 'asc' ? '▲' : '▼') : ''}
                      </th>
                      <th style={{ cursor: 'pointer' }} onClick={() => handleInvoiceSort('amount')}>
                        Amount {invoiceSort.column === 'amount' ? (invoiceSort.direction === 'asc' ? '▲' : '▼') : ''}
                      </th>
                      <th style={{ cursor: 'pointer' }} onClick={() => handleInvoiceSort('payment_status')}>
                        Status {invoiceSort.column === 'payment_status' ? (invoiceSort.direction === 'asc' ? '▲' : '▼') : ''}
                      </th>
                      <th style={{ cursor: 'pointer' }} onClick={() => handleInvoiceSort('invoice_date')}>
                        Invoice Date {invoiceSort.column === 'invoice_date' ? (invoiceSort.direction === 'asc' ? '▲' : '▼') : ''}
                      </th>
                      <th style={{ cursor: 'pointer' }} onClick={() => handleInvoiceSort('dispatch_date')}>
                        Dispatch Date {invoiceSort.column === 'dispatch_date' ? (invoiceSort.direction === 'asc' ? '▲' : '▼') : ''}
                      </th>
                      <th style={{ cursor: 'pointer' }} onClick={() => handleInvoiceSort('due_date')}>
                        Due Date {invoiceSort.column === 'due_date' ? (invoiceSort.direction === 'asc' ? '▲' : '▼') : ''}
                      </th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedInvoices.map((invoice) => (
                      <tr key={invoice.id}>
                        <td>
                          {invoice.erp_id ? (
                            <code>#{invoice.erp_id}</code>
                          ) : (
                            <Button
                              variant="warning"
                              size="sm"
                              onClick={() => handleMatchInvoice(invoice)}
                            >
                              <i className="bi bi-link-45deg me-1"></i>
                              Match Invoice
                            </Button>
                          )}
                        </td>
                        <td>
                          <div className="fw-medium">{invoice.organization_name}({invoice.organization_erp_id})</div>
                        </td>
                        <td>
                          <div className="fw-medium">{invoice.description}</div>
                        </td>
                        <td>
                          <span className="fw-medium">
                            {invoice.invoice_currency_code} {parseFloat(invoice.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        </td>
                        <td>
                          <Badge bg={getInvoiceStatusBadge(invoice.payment_status.toLowerCase())}>
                            {invoice.payment_status}
                          </Badge>
                        </td>
                        <td>{apiService.formatDate(invoice.invoice_date)}</td>
                        <td>{invoice.dispatch_date ? apiService.formatDate(invoice.dispatch_date) : '-'}</td>
                        <td>{invoice.due_date ? apiService.formatDate(invoice.due_date) : '-'}</td>
                        <td>
                          <div className="d-flex gap-1">
                            <Button variant="outline-primary" size="sm" title="View">
                              <i className="bi bi-eye"></i>
                            </Button>
                            <Button variant="outline-secondary" size="sm" title="Download">
                              <i className="bi bi-download"></i>
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
              {renderPagination(invoicePage, invoiceTotalPages, setInvoicePage)}
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="payments" title={`Payments (${filteredPayments.length})`}>


          <Card>
            <Card.Body className="p-0">
              {filteredPayments.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  <i className="bi bi-credit-card display-1"></i>
                  <div className="mt-2">{selectedOrganization ? 'No payments found for this organization' : 'No payments found'}</div>
                </div>
              ) : (
                <Table responsive hover className="mb-0">
                  <thead>
                    <tr>
                      <th style={{ cursor: 'pointer' }} onClick={() => handlePaymentSort('erp_id')}>
                        ERP ID {paymentSort.column === 'erp_id' ? (paymentSort.direction === 'asc' ? '▲' : '▼') : ''}
                      </th>
                      <th style={{ cursor: 'pointer' }} onClick={() => handlePaymentSort('vendor_transaction_id')}>
                        Transaction Code {paymentSort.column === 'vendor_transaction_id' ? (paymentSort.direction === 'asc' ? '▲' : '▼') : ''}
                      </th>
                      <th style={{ cursor: 'pointer' }} onClick={() => handlePaymentSort('unique_invoices_allocated')}>
                        Invoice Count {paymentSort.column === 'unique_invoices_allocated' ? (paymentSort.direction === 'asc' ? '▲' : '▼') : ''}
                      </th>
                      <th style={{ cursor: 'pointer' }} onClick={() => handlePaymentSort('organization_name')}>
                        Organization {paymentSort.column === 'organization_name' ? (paymentSort.direction === 'asc' ? '▲' : '▼') : ''}
                      </th>
                      <th style={{ cursor: 'pointer' }} onClick={() => handlePaymentSort('amount')}>
                        Received {paymentSort.column === 'amount' ? (paymentSort.direction === 'asc' ? '▲' : '▼') : ''}
                      </th>
                      <th style={{ cursor: 'pointer' }} onClick={() => handlePaymentSort('unallocated_amount')}>
                        Unallocated {paymentSort.column === 'unallocated_amount' ? (paymentSort.direction === 'asc' ? '▲' : '▼') : ''}
                      </th>
                      <th style={{ cursor: 'pointer' }} onClick={() => handlePaymentSort('total_allocated_amount')}>
                        Allocated {paymentSort.column === 'total_allocated_amount' ? (paymentSort.direction === 'asc' ? '▲' : '▼') : ''}
                      </th>
                      <th style={{ cursor: 'pointer' }} onClick={() => handlePaymentSort('allocation_status')}>
                        Status {paymentSort.column === 'allocation_status' ? (paymentSort.direction === 'asc' ? '▲' : '▼') : ''}
                      </th>
                      <th style={{ cursor: 'pointer' }} onClick={() => handlePaymentSort('transaction_date')}>
                        Payment Date {paymentSort.column === 'transaction_date' ? (paymentSort.direction === 'asc' ? '▲' : '▼') : ''}
                      </th>
                      <th style={{ cursor: 'pointer' }} onClick={() => handlePaymentSort('method')}>
                        Method {paymentSort.column === 'method' ? (paymentSort.direction === 'asc' ? '▲' : '▼') : ''}
                      </th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedPayments.map((payment) => (
                      <tr key={payment.id}>
                        <td>
                          <code>{payment.erp_id}</code>
                        </td>
                        <td>
                          <code>{payment.vendor_transaction_id}</code>
                        </td>
                        <td>
                          <code>{payment.unique_invoices_allocated}</code>
                        </td>
                        <td>
                          <div className="fw-medium">{payment.organization_name}</div>
                        </td>
                        <td>
                          <span className="fw-medium">
                            {payment.currency_code} {parseFloat(payment.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        </td>
                        <td>
                          <span className="fw-medium">
                            {payment.currency_code} {parseFloat(payment.unallocated_amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        </td>
                        <td>
                          <span className="fw-medium">
                            {payment.currency_code} {parseFloat(payment.total_allocated_amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        </td>
                        <td>
                          <Badge bg={getPaymentStatusBadge(payment.allocation_status)}>
                            {payment.allocation_status}
                          </Badge>
                        </td>
                        <td>{apiService.formatDate(payment.transaction_date)}</td>
                        <td>
                          <Badge bg="info">{payment.method}</Badge>
                        </td>
                        <td>
                          <div className="d-flex gap-1">
                            <Button variant="outline-primary" size="sm" title="View">
                              <i className="bi bi-eye"></i>
                            </Button>
                            <Button variant="outline-secondary" size="sm" title="Receipt">
                              <i className="bi bi-receipt"></i>
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
              {renderPagination(paymentPage, paymentTotalPages, setPaymentPage)}
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>

      {/* Match Invoice Modal */}
      <Modal show={showMatchModal} onHide={() => setShowMatchModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-link-45deg me-2"></i>
            Match Invoice to ERP
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedInvoiceToMatch && (
            <>
              <Alert variant="info">
                <strong>Invoice to Match:</strong>
                <div className="mt-2">
                  <div><strong>Organization:</strong> {selectedInvoiceToMatch.organization_name}</div>
                  <div><strong>Description:</strong> {selectedInvoiceToMatch.description}</div>
                  <div><strong>Amount:</strong> {selectedInvoiceToMatch.invoice_currency_code} {parseFloat(selectedInvoiceToMatch.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                  <div><strong>Date:</strong> {apiService.formatDate(selectedInvoiceToMatch.invoice_date)}</div>
                </div>
              </Alert>

              {matchError && (
                <Alert variant="danger" dismissible onClose={() => setMatchError('')}>
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  {matchError}
                </Alert>
              )}

              {loadingErpInvoices ? (
                <div className="text-center py-4">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading ERP invoices...</span>
                  </Spinner>
                  <div className="mt-2">Loading ERP invoices...</div>
                </div>
              ) : erpInvoicesError ? (
                <Alert variant="danger">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  {erpInvoicesError}
                </Alert>
              ) : erpInvoices.length === 0 ? (
                <Alert variant="info">
                  <i className="bi bi-info-circle me-2"></i>
                  No ERP invoices available for matching.
                </Alert>
              ) : (
                <>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Select ERP Invoice to Match:</strong></Form.Label>
                    <Form.Select
                      value={selectedErpInvoice}
                      onChange={(e) => setSelectedErpInvoice(e.target.value)}
                    >
                      <option value="">-- Select an ERP Invoice --</option>
                      {erpInvoices.map((erpInv) => (
                        <option key={erpInv.id} value={erpInv.id}>
                          {erpInv.id} - {erpInv.ref} - {getCustomerNameBySocid(erpInv.socid)} - {erpInv.multicurrency_code} {parseFloat(erpInv.total_ttc).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ({apiService.formatDate(erpInv.date)})
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <div className="mt-3">
                    <h6>Available ERP Invoices:</h6>
                    <Table hover size="sm" className="mt-2">
                      <thead>
                        <tr>
                          <th>ERP ID</th>
                          <th>Reference</th>
                          <th>Customer</th>
                          <th>Amount</th>
                          <th>Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {erpInvoices.map((erpInv) => (
                          <tr
                            key={erpInv.id}
                            style={{ cursor: 'pointer' }}
                            onClick={() => setSelectedErpInvoice(erpInv.id)}
                            className={selectedErpInvoice === erpInv.id ? 'table-active' : ''}
                          >
                            <td><code>{erpInv.id}</code></td>
                            <td>{erpInv.ref}</td>
                            <td>{getCustomerNameBySocid(erpInv.socid)}</td>
                            <td>{erpInv.multicurrency_code} {parseFloat(erpInv.total_ttc).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                            <td>{apiService.formatDate(erpInv.date)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowMatchModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleMatchConfirm}
            disabled={!selectedErpInvoice || matchingInvoice}
          >
            {matchingInvoice ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Matching...
              </>
            ) : (
              <>
                <i className="bi bi-check-circle me-2"></i>
                Confirm Match
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Billing;
